import {
  createGroupMemberTag as createGroupMemberTagApi,
  deleteGroupMemberTag as deleteGroupMemberTagApi,
  getGroupMemberTagList,
  updateGroupMemberTag as updateGroupMemberTagApi,
} from '../../api/groupMemberTagApi';
import { GroupMemberTagData } from '../../dto/EntityRepository';
import { CreateTagReq, DeleteTagReq, UpdateTagReq } from '../../dto/ReqDtoRepository';
import { TagListResponse, TagResponse } from '../../dto/ResDtoRepository';
import { useGroupMemberTagStore } from '../../store/groupMemberTagStore';
import { useGroupMemberStore } from '../../store/groupMemberStore';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { useApiWrapper } from './apiWrapper';
import { useModalStore } from '../../store/portal/modalStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UseGroupMemberTagStateResult {
  tags: GroupMemberTagData[];
  selectedTags: GroupMemberTagData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseGroupMemberTagActionsResult {
  getGroupMemberTagList: () => Promise<void>;
  createGroupMemberTag: (data: CreateTagReq) => Promise<void>;
  updateGroupMemberTag: (data: UpdateTagReq) => Promise<void>;
  deleteGroupMemberTag: (data: DeleteTagReq) => Promise<void>;
  addSelectedTag: (tag: GroupMemberTagData) => void;
  removeSelectedTag: (tag: GroupMemberTagData) => void;
  clearGroupMemberTags: () => void;
}

type UseGroupMemberTagResult = UseGroupMemberTagStateResult & UseGroupMemberTagActionsResult;

export const useGroupMemberTagState = (): UseGroupMemberTagStateResult => {
  const tags = useGroupMemberTagStore((state) => state.tags);
  const selectedTags = useGroupMemberTagStore((state) => state.selectedTags);
  const listOwnerAui = useGroupMemberTagStore((state) => state.listOwnerAui);
  const listScope = useGroupMemberTagStore((state) => state.listScope);
  const status = useGroupMemberTagStore((state) => state.status);

  return { tags, selectedTags, listOwnerAui, listScope, status };
};

export const useGroupMemberTagActions = (): UseGroupMemberTagActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setTags = useGroupMemberTagStore((s) => s.setTags);
  const beginGroupMemberTagListFetch = useGroupMemberTagStore(
    (s) => s.beginGroupMemberTagListFetch,
  );
  const setGroupMemberTagListError = useGroupMemberTagStore(
    (s) => s.setGroupMemberTagListError,
  );
  const afterCreateTag = useGroupMemberTagStore((s) => s.afterCreateTag);
  const afterUpdateTag = useGroupMemberTagStore((s) => s.afterUpdateTag);
  const afterDeleteTag = useGroupMemberTagStore((s) => s.afterDeleteTag);
  const addSelectedTag = useGroupMemberTagStore((s) => s.addSelectedTag);
  const removeSelectedTag = useGroupMemberTagStore((s) => s.removeSelectedTag);
  const clearGroupMemberTags = useGroupMemberTagStore((s) => s.clearGroupMemberTags);
  const withApiHandler = useApiWrapper();
  const syncGroupMemberTag = useGroupMemberStore((s) => s.syncGroupMemberTag);
  const removeGroupMemberTag = useGroupMemberStore((s) => s.removeGroupMemberTag);
  const { clearModal } = useModalStore();
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();

  const handleGetGroupMemberTagListSuccess = (
    response: TagListResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setTags(response.data, ownerAui, scope);
  };

  const handleCreateGroupMemberTagSuccess = (response: TagResponse) => {
    afterCreateTag(response.data);
    setUpdatedTempAlert();
    clearModal();
  };

  const handleUpdateGroupMemberTagSuccess = (response: TagResponse) => {
    afterUpdateTag(response.data);
    syncGroupMemberTag(response.data);
    setUpdatedTempAlert();
  };

  const handleDeleteGroupMemberTagSuccess = (tagId: number) => {
    afterDeleteTag(tagId);
    removeGroupMemberTag(tagId);
    setDeletedTempAlert();
  };

  const fetchGroupMemberTagList = async (aui: string) => {
    beginGroupMemberTagListFetch(aui, 'public');
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getGroupMemberTagList(aui),
        (response) => handleGetGroupMemberTagListSuccess(response, aui, 'public'),
      );
    } catch (error) {
      setGroupMemberTagListError();
      throw error;
    }
  };

  const handleGroupMemberTagRequest = async (
    action: 'get' | 'create' | 'update' | 'delete',
    data?: CreateTagReq | UpdateTagReq | DeleteTagReq,
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createGroupMemberTagApi(aui, data as CreateTagReq),
            handleCreateGroupMemberTagSuccess,
          );
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateGroupMemberTagApi(aui, data as UpdateTagReq),
            handleUpdateGroupMemberTagSuccess,
          );
        case 'delete': {
          const tagId = (data as DeleteTagReq).tagId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteGroupMemberTagApi(aui, tagId),
            () => handleDeleteGroupMemberTagSuccess(tagId),
          );
        }
        case 'get':
        default:
          return fetchGroupMemberTagList(aui);
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  return {
    getGroupMemberTagList: () => handleGroupMemberTagRequest('get'),
    createGroupMemberTag: (data: CreateTagReq) => handleGroupMemberTagRequest('create', data),
    updateGroupMemberTag: (data: UpdateTagReq) => handleGroupMemberTagRequest('update', data),
    deleteGroupMemberTag: (data: DeleteTagReq) => handleGroupMemberTagRequest('delete', data),
    addSelectedTag,
    removeSelectedTag,
    clearGroupMemberTags,
  };
};

/** 기존 호출부 호환: state + actions */
export const useGroupMemberTag = (): UseGroupMemberTagResult => ({
  ...useGroupMemberTagState(),
  ...useGroupMemberTagActions(),
});
